import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectToasts, selectVisibleToasts } from './selectors';
import { showToast, closeToast } from './slice';
import { ToastToDisplay, ToastType } from './state';
import { registerToastAction } from './toastActions';

export const useToasts = () => useSelector(selectToasts);

export const useVisibleToasts = () => useSelector(selectVisibleToasts);

export const useShowToast = () => {
  const dispatch = useDispatch();

  const show = useCallback(
    (toast: ToastToDisplay) => {
      dispatch(showToast(toast));
    },
    [dispatch]
  );

  const showSuccess = useCallback(
    (description: string, title?: string) => {
      dispatch(
        showToast({
          type: ToastType.success,
          description,
          title,
          autoHide: true,
        })
      );
    },
    [dispatch]
  );

  const showError = useCallback(
    (description: string, title?: string) => {
      dispatch(
        showToast({
          type: ToastType.danger,
          description,
          title,
          autoHide: false,
        })
      );
    },
    [dispatch]
  );

  const showInfo = useCallback(
    (description: string, title?: string) => {
      dispatch(
        showToast({
          type: ToastType.info,
          description,
          title,
          autoHide: true,
        })
      );
    },
    [dispatch]
  );

  const showWarning = useCallback(
    (description: string, title?: string) => {
      dispatch(
        showToast({
          type: ToastType.warning,
          description,
          title,
          autoHide: true,
        })
      );
    },
    [dispatch]
  );

  return {
    show,
    showSuccess,
    showError,
    showInfo,
    showWarning,
  };
};

export const useCloseToast = () => {
  const dispatch = useDispatch();

  return useCallback(
    (toastId: string) => {
      dispatch(closeToast({ toastId }));
    },
    [dispatch]
  );
};

/** Registers the handler of a toast action id while the component is mounted. */
export const useToastAction = (actionId: string, handler: () => void) => {
  useEffect(() => registerToastAction(actionId, handler), [actionId, handler]);
};
